import { useRef, useEffect, RefObject } from 'react';

export interface SwipeGestureOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  threshold?: number; // Minimum distance in pixels to trigger swipe (default: 50)
  preventDefaultTouchMove?: boolean; // Prevent scroll during horizontal swipe
}

interface TouchPosition {
  x: number;
  y: number;
  time: number;
}

/**
 * Custom hook to detect swipe gestures on touch devices
 * @param elementRef - React ref to the element to attach swipe listeners
 * @param options - Swipe gesture configuration
 */
export const useSwipeGesture = <T extends HTMLElement>(
  elementRef: RefObject<T>,
  options: SwipeGestureOptions
): void => {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    threshold = 50,
    preventDefaultTouchMove = false,
  } = options;

  const touchStartRef = useRef<TouchPosition | null>(null);
  const touchEndRef = useRef<TouchPosition | null>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };
      touchEndRef.current = null;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartRef.current) return;

      const touch = e.touches[0];
      touchEndRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };

      // Calculate deltas
      const diffX = Math.abs(touchStartRef.current.x - touch.clientX);
      const diffY = Math.abs(touchStartRef.current.y - touch.clientY);

      // Prevent default for horizontal swipes if requested
      // This allows drawer swiping without triggering page scroll
      if (preventDefaultTouchMove && diffX > diffY && diffX > 10) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartRef.current || !touchEndRef.current) {
        touchStartRef.current = null;
        touchEndRef.current = null;
        return;
      }

      const startX = touchStartRef.current.x;
      const startY = touchStartRef.current.y;
      const endX = touchEndRef.current.x;
      const endY = touchEndRef.current.y;
      const timeDiff = touchEndRef.current.time - touchStartRef.current.time;

      // Calculate distances
      const diffX = endX - startX;
      const diffY = endY - startY;
      const absDiffX = Math.abs(diffX);
      const absDiffY = Math.abs(diffY);

      // Determine if swipe is primarily horizontal or vertical
      const isHorizontal = absDiffX > absDiffY;

      // Calculate velocity (pixels per ms)
      const velocity = isHorizontal
        ? absDiffX / (timeDiff || 1)
        : absDiffY / (timeDiff || 1);

      // Minimum velocity to consider it a swipe (0.3 px/ms)
      const minVelocity = 0.3;

      // Check if swipe meets threshold and velocity requirements
      if (velocity >= minVelocity) {
        if (isHorizontal && absDiffX >= threshold) {
          // Horizontal swipe
          if (diffX > 0) {
            onSwipeRight?.();
          } else {
            onSwipeLeft?.();
          }
        } else if (!isHorizontal && absDiffY >= threshold) {
          // Vertical swipe
          if (diffY > 0) {
            onSwipeDown?.();
          } else {
            onSwipeUp?.();
          }
        }
      }

      // Reset
      touchStartRef.current = null;
      touchEndRef.current = null;
    };

    // Attach event listeners
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, {
      passive: !preventDefaultTouchMove,
    });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Cleanup
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [
    elementRef,
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    threshold,
    preventDefaultTouchMove,
  ]);
};
